import React, { useEffect, useState, useCallback } from 'react';
import URLManager from '../funcionalidadeNavbar/URLManager';
import ConfirmationModal from '../components/ConfirmationModal';
import './ListagemPedidosVenda.css';

const ListagemPedidosVenda = ({ onSelecionarPedido, onEditarPedido, onNovoPedido }) => {
  const _url = URLManager.getBaseURL();
  
  const [pedidos, setPedidos] = useState([]);
  const [pedidosFiltrados, setPedidosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [feedback, setFeedback] = useState(null);
  
  // Estados de filtro
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  
  // Modal de confirmação de exclusão
  const [modalExcluir, setModalExcluir] = useState(false);
  const [pedidoParaExcluir, setPedidoParaExcluir] = useState(null);
  
  // Modal de histórico
  const [modalHistorico, setModalHistorico] = useState(false);
  const [pedidoHistorico, setPedidoHistorico] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);

  // Modal de confirmação para ações
  const [modalConfirm, setModalConfirm] = useState({
    open: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null,
    confirmText: 'Confirmar',
    cancelText: 'Cancelar'
  });

  // Função para mostrar mensagem
  const mostrarMensagem = (mensagem, tipo = 'success') => {
    setFeedback({ type: tipo, message: mensagem });
    setTimeout(() => setFeedback(null), 5000);
  };

  // Carregar pedidos
  const carregarPedidos = useCallback(async () => {
    setLoading(true);
    setErro(null);
    
    try {
      const res = await fetch(`${_url}/pedido-venda/lista`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
        }
      });
      
      if (!res.ok) throw new Error('Erro ao buscar pedidos');
      const data = await res.json();
      
      // Buscar histórico para cada pedido para calcular status correto
      const pedidosComHistorico = await Promise.all(
        (data || []).map(async (pedido) => {
          try {
            const histRes = await fetch(`${_url}/pedido-venda/historico/${pedido.id}`, {
              headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
              }
            });
            
            if (histRes.ok) {
              const historico = await histRes.json();
              return { ...pedido, historico };
            }
            return pedido;
          } catch {
            return pedido;
          }
        })
      );
      
      setPedidos(pedidosComHistorico);
      setPedidosFiltrados(pedidosComHistorico);
    } catch (err) {
      setErro(err.message || 'Erro ao buscar pedidos');
      mostrarMensagem(err.message || 'Erro ao buscar pedidos', 'error');
    } finally {
      setLoading(false);
    }
  }, [_url]);

  useEffect(() => {
    carregarPedidos();
  }, [carregarPedidos]);

  // Aplicar filtros
  useEffect(() => {
    let resultado = [...pedidos];

    // Filtro por cliente
    if (filtroCliente) {
      resultado = resultado.filter(p => 
        obterNomeCliente(p).toLowerCase().includes(filtroCliente.toLowerCase())
      );
    }

    // Filtro por status
    if (filtroStatus) {
      resultado = resultado.filter(p => p.status === filtroStatus);
    }

    // Filtro por data início
    if (filtroDataInicio) {
      resultado = resultado.filter(p => p.dataPedido >= filtroDataInicio);
    }

    // Filtro por data fim
    if (filtroDataFim) {
      resultado = resultado.filter(p => p.dataPedido <= filtroDataFim);
    }

    setPedidosFiltrados(resultado);
  }, [filtroCliente, filtroStatus, filtroDataInicio, filtroDataFim, pedidos]);

  // Limpar filtros
  const limparFiltros = () => {
    setFiltroCliente('');
    setFiltroStatus('');
    setFiltroDataInicio('');
    setFiltroDataFim('');
  };

  // Formatar data
  const formatarData = (data) => {
    if (!data) return '-';
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR');
  };

  // Parsear grade de numeração
  const parseGradeNumeracao = (gradeJson) => {
    try {
      if (!gradeJson) return {};
      if (typeof gradeJson === 'string') {
        return JSON.parse(gradeJson);
      }
      return gradeJson;
    } catch (e) {
      return {};
    }
  };

  // Calcular total de pares
  const calcularTotalPares = (pedido) => {
    if (pedido.quantidadeTotal) return pedido.quantidadeTotal;
    
    const grade = parseGradeNumeracao(pedido.gradeNumeracao);
    return Object.values(grade).reduce((soma, qtd) => soma + Number(qtd || 0), 0);
  };

  // Obter nome do cliente considerando os possíveis formatos vindos do backend
  const obterNomeCliente = (pedido = {}) => {
    const clienteObj = pedido.idCliente || pedido.cliente; // backend pode retornar objeto em idCliente ou string em cliente
    if (typeof clienteObj === 'string') return clienteObj || '-';
    return (
      clienteObj?.nomeCliente ||
      clienteObj?.nome ||
      clienteObj?.nomeFantasia ||
      pedido.cliente ||
      '-'
    );
  };

  // Confirmar exclusão
  const confirmarExclusao = (pedido) => {
    setPedidoParaExcluir(pedido);
    setModalExcluir(true);
  };

  // Excluir pedido
  const excluirPedido = async () => {
    if (!pedidoParaExcluir) return;

    try {
      const res = await fetch(`${_url}/pedido-venda/remover/${pedidoParaExcluir.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
        }
      });

      if (!res.ok) {
        const error = await res.text();
        throw new Error(error || 'Erro ao excluir pedido');
      }

      mostrarMensagem('Pedido excluído com sucesso!', 'success');
      carregarPedidos();
    } catch (err) {
      mostrarMensagem(err.message || 'Erro ao excluir pedido', 'error');
    } finally {
      setModalExcluir(false);
      setPedidoParaExcluir(null);
    }
  };

  // Badge de status
  const getBadgeStatus = (status) => {
    const statusMap = {
      'Pendente': 'badge-pendente',
      'Em Produção': 'badge-producao',
      'Concluído': 'badge-concluido',
      'Cancelado': 'badge-cancelado'
    };
    return statusMap[status] || 'badge-default';
  };

  // Calcular status real do pedido baseado nas ordens de produção
  const calcularStatusPedido = (pedido) => {
    // Se não houver histórico carregado, usar o status do backend
    if (!pedido.historico || pedido.historico.length === 0) {
      return pedido.status || 'Pendente';
    }

    // Verificar se todas as ordens estão concluídas
    const todasConcluidas = pedido.historico.every(op => op.ordemConcluida);
    if (todasConcluidas) {
      return 'Concluído';
    }

    // Verificar se alguma ordem está em produção (não concluída e com data de término)
    const algumaNaoIniciada = pedido.historico.every(op => !op.ordemConcluida && !op.previsaoTermino);
    if (algumaNaoIniciada) {
      return 'Pendente';
    }

    // Se há ordens em andamento
    return 'Em Produção';
  };

  // Abrir modal de histórico
  const abrirHistorico = async (pedido) => {
    setPedidoHistorico(pedido);
    setModalHistorico(true);
    setLoadingHistorico(true);
    setHistorico([]);
    
    try {
      const res = await fetch(`${_url}/pedido-venda/historico/${pedido.id}`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
        }
      });
      
      if (!res.ok) throw new Error('Erro ao buscar histórico');
      const data = await res.json();
      console.log('Histórico recebido do backend:', data); // DEBUG
      setHistorico(data || []);
    } catch (err) {
      mostrarMensagem(err.message || 'Erro ao buscar histórico', 'error');
    } finally {
      setLoadingHistorico(false);
    }
  };

  // Fechar modal de histórico
  const fecharHistorico = () => {
    setModalHistorico(false);
    setPedidoHistorico(null);
    setHistorico([]);
  };

  // Parsear grade JSON
  const parseGrade = (gradeJson) => {
    try {
      if (!gradeJson) return {};
      if (typeof gradeJson === 'string') return JSON.parse(gradeJson);
      return gradeJson;
    } catch {
      return {};
    }
  };

  // Formatar grade para exibição
  const formatarGrade = (gradeObj) => {
    if (!gradeObj || Object.keys(gradeObj).length === 0) return 'N/A';
    return Object.entries(gradeObj)
      .filter(([_, qtd]) => qtd > 0)
      .map(([num, qtd]) => `${num}:${qtd}`)
      .join(', ');
  };

  // Calcular status da OP baseado em data e conclusão
  const getStatusBadgeOP = (item) => {
    if (item.ordemConcluida) return 'status-finalizado';
    
    if (item.previsaoTermino) {
      const dataFinal = new Date(item.previsaoTermino);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      dataFinal.setHours(0, 0, 0, 0);
      
      if (dataFinal < hoje) return 'status-atrasado';
      if (dataFinal.getTime() === hoje.getTime()) return 'status-producao';
    }
    
    return 'status-pendente';
  };

  const getStatusTextoOP = (item) => {
    if (item.ordemConcluida) return 'FINALIZADO';
    
    if (item.previsaoTermino) {
      const dataFinal = new Date(item.previsaoTermino);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      dataFinal.setHours(0, 0, 0, 0);
      
      if (dataFinal < hoje) return 'ATRASADO';
      if (dataFinal.getTime() === hoje.getTime()) return 'EM PRODUÇÃO';
    }
    
    return 'PENDENTE';
  };

  // Concluir ordem de produção
  const concluirOrdemProducao = async (ordemId) => {
    setModalConfirm({
      open: true,
      title: 'Concluir Ordem de Produção',
      message: 'Confirma a conclusão desta Ordem de Produção?',
      type: 'warning',
      confirmText: 'Sim, Concluir',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        setModalConfirm(prev => ({ ...prev, open: false }));
        try {
          const observacao = prompt('Observação (opcional):');
          
          const res = await fetch(`${_url}/ordemProducao/concluir/${ordemId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
            },
            body: JSON.stringify({ observacao: observacao || '' })
          });
          
          if (!res.ok) {
            const erro = await res.json();
            throw new Error(erro.erro || 'Erro ao concluir ordem');
          }
          
          mostrarMensagem('Ordem de Produção concluída com sucesso!', 'success');
          
          // Recarregar histórico
          if (pedidoHistorico) {
            await abrirHistorico(pedidoHistorico);
          }
          
          // Recarregar lista de pedidos para atualizar o status
          carregarPedidos();
          
        } catch (err) {
          mostrarMensagem(err.message || 'Erro ao concluir ordem', 'error');
        }
      }
    });
  };

  // Emitir NF-e
  const emitirNFe = async (pedidoId) => {
    setModalConfirm({
      open: true,
      title: 'Emitir Nota Fiscal',
      message: 'Confirma a emissão da Nota Fiscal Eletrônica para este pedido?',
      type: 'warning',
      confirmText: 'Sim, Emitir',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        setModalConfirm(prev => ({ ...prev, open: false }));
        try {
          const res = await fetch(`${_url}/pedido-venda/${pedidoId}/emitir-nfe`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
            }
          });
          
          if (!res.ok) {
            const erro = await res.text();
            throw new Error(erro || 'Erro ao emitir NF-e');
          }
          
          await res.text();
          mostrarMensagem('Nota Fiscal emitida com sucesso!', 'success');
          
        } catch (err) {
          mostrarMensagem(err.message || 'Erro ao emitir NF-e', 'error');
        }
      }
    });
  };

  // Reabrir ordem de produção
  const reabrirOrdemProducao = async (ordemId) => {
    setModalConfirm({
      open: true,
      title: 'Reabrir Ordem de Produção',
      message: 'Confirma a reabertura desta Ordem de Produção?',
      type: 'warning',
      confirmText: 'Sim, Reabrir',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        setModalConfirm(prev => ({ ...prev, open: false }));
        try {
          const res = await fetch(`${_url}/ordemProducao/reabrir/${ordemId}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
            }
          });
          
          if (!res.ok) {
            const erro = await res.json();
            throw new Error(erro.erro || 'Erro ao reabrir ordem');
          }
          
          mostrarMensagem('Ordem de Produção reaberta com sucesso!', 'success');
          
          // Recarregar histórico
          if (pedidoHistorico) {
            await abrirHistorico(pedidoHistorico);
          }
          
          // Recarregar lista de pedidos
          carregarPedidos();
          
        } catch (err) {
          mostrarMensagem(err.message || 'Erro ao reabrir ordem', 'error');
        }
      }
    });
  };

  return (
    <div className="listagem-pedidos-container">
      <div className="listagem-header">
        <h2>Pedidos de Venda</h2>
        {onNovoPedido && (
          <button className="btn-novo-pedido" onClick={onNovoPedido}>
            + Novo Pedido
          </button>
        )}
      </div>

      {feedback && (
        <div className={`feedback-message ${feedback.type}`}>
          {feedback.message}
        </div>
      )}

      {/* Filtros */}
      <div className="filtros-container">
        <div className="filtros-grid">
          <input
            type="text"
            placeholder="Filtrar por cliente..."
            value={filtroCliente}
            onChange={e => setFiltroCliente(e.target.value)}
            className="filtro-input"
          />
          
          <select
            value={filtroStatus}
            onChange={e => setFiltroStatus(e.target.value)}
            className="filtro-select"
          >
            <option value="">Todos os status</option>
            <option value="Pendente">Pendente</option>
            <option value="Em Produção">Em Produção</option>
            <option value="Concluído">Concluído</option>
            <option value="Cancelado">Cancelado</option>
          </select>

          <input
            type="date"
            placeholder="Data início"
            value={filtroDataInicio}
            onChange={e => setFiltroDataInicio(e.target.value)}
            className="filtro-input"
          />

          <input
            type="date"
            placeholder="Data fim"
            value={filtroDataFim}
            onChange={e => setFiltroDataFim(e.target.value)}
            className="filtro-input"
          />

          <button onClick={limparFiltros} className="btn-limpar-filtros">
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* Tabela */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Carregando pedidos...</p>
        </div>
      ) : erro ? (
        <div className="erro-container">
          <p>{erro}</p>
          <button onClick={carregarPedidos} className="btn-recarregar">
            Tentar Novamente
          </button>
        </div>
      ) : pedidosFiltrados.length === 0 ? (
        <div className="vazio-container">
          <p>Nenhum pedido encontrado</p>
          {(filtroCliente || filtroStatus || filtroDataInicio || filtroDataFim) && (
            <button onClick={limparFiltros} className="btn-limpar-filtros">
              Limpar Filtros
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="info-resultado">
            <span>{pedidosFiltrados.length} pedido(s) encontrado(s)</span>
          </div>
          
          <div className="tabela-responsive">
            <table className="tabela-pedidos">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Data</th>
                  <th>Status</th>
                  <th>Produto</th>
                  <th>Cor</th>
                  <th>Total Pares</th>
                  <th>Valor Total R$</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pedidosFiltrados.map(pedido => (
                  <tr key={pedido.id}>
                    <td data-label="ID">{pedido.id}</td>
                    <td data-label="Cliente">{obterNomeCliente(pedido)}</td>
                    <td data-label="Data">{formatarData(pedido.dataPedido)}</td>
                    <td data-label="Status">
                      <span className={`badge ${getBadgeStatus(calcularStatusPedido(pedido))}`}>
                        {calcularStatusPedido(pedido)}
                      </span>
                    </td>
                    <td data-label="Produto">
                      {pedido.idProduto?.nomeModelo || pedido.idProduto?.nome || '-'}
                    </td>
                    <td data-label="Cor">{pedido.cor || '-'}</td>
                    <td data-label="Total Pares">{calcularTotalPares(pedido)}</td>
                    <td data-label="Valor Total R$">{"R$ " + pedido.valorTotal.toFixed(2)}</td>
                    <td data-label="Ações">
                      <div className="acoes-buttons">
                        <button
                          className="btn-acao btn-historico"
                          onClick={() => abrirHistorico(pedido)}
                          title="Ver histórico de produção"
                        >
                          📜
                        </button>
                        {onSelecionarPedido && (
                          <button
                            className="btn-acao btn-visualizar"
                            onClick={() => onSelecionarPedido(pedido.id)}
                            title="Visualizar detalhes"
                          >
                            👁️
                          </button>
                        )}
                        {onEditarPedido && (
                          <button
                            className="btn-acao btn-editar"
                            onClick={() => onEditarPedido(pedido)}
                            title="Editar pedido"
                          >
                            ✏️
                          </button>
                        )}
                        <button
                          className="btn-acao btn-nfe"
                          onClick={() => emitirNFe(pedido.id)}
                          title="Emitir Nota Fiscal"
                        >
                          📄
                        </button>
                        <button
                          className="btn-acao btn-excluir"
                          onClick={() => confirmarExclusao(pedido)}
                          title="Excluir pedido"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modal de confirmação de exclusão */}
      {modalExcluir && (
        <div className="modal-overlay" onClick={() => setModalExcluir(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Confirmar Exclusão</h3>
            <p>Tem certeza que deseja excluir o pedido do cliente <strong>{obterNomeCliente(pedidoParaExcluir)}</strong>?</p>
            <p className="modal-aviso">Esta ação não pode ser desfeita.</p>
            <div className="modal-acoes">
              <button className="btn-cancelar" onClick={() => setModalExcluir(false)}>
                Cancelar
              </button>
              <button className="btn-confirmar-exclusao" onClick={excluirPedido}>
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de histórico de produção */}
      {modalHistorico && pedidoHistorico && (
        <div className="modal-overlay" onClick={fecharHistorico}>
          <div className="modal-content modal-historico" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Histórico de Produção - Pedido #{pedidoHistorico.id}</h3>
              <button className="btn-fechar-modal" onClick={fecharHistorico}>×</button>
            </div>

            <div className="historico-info">
              <div className="info-row">
                <span><strong>Cliente:</strong> {obterNomeCliente(pedidoHistorico)}</span>
                <span><strong>Data:</strong> {formatarData(pedidoHistorico.dataPedido)}</span>
              </div>
              <div className="info-row">
                <span><strong>Produto:</strong> {pedidoHistorico.idProduto?.nomeModelo || '-'}</span>
                <span><strong>Cor:</strong> {pedidoHistorico.cor || '-'}</span>
              </div>
              <div className="info-producao">
                <div className="producao-card">
                  <span className="producao-label">Total do Pedido</span>
                  <span className="producao-valor">{pedidoHistorico.quantidadeTotal || 0}</span>
                </div>
                <div className="producao-card producao-produzida">
                  <span className="producao-label">Já Programado</span>
                  <span className="producao-valor">{pedidoHistorico.quantidadeProduzida || 0}</span>
                </div>
                <div className={`producao-card ${
                  (() => {
                    const total = pedidoHistorico.quantidadeTotal || 0;
                    const produzido = pedidoHistorico.quantidadeProduzida || 0;
                    const restante = Math.max(0, total - produzido);
                    return restante === 0 ? 'producao-finalizada' : 'producao-restante';
                  })()
                }`}>
                  <span className="producao-label">Restante</span>
                  <span className="producao-valor">
                    {(() => {
                      const total = pedidoHistorico.quantidadeTotal || 0;
                      const produzido = pedidoHistorico.quantidadeProduzida || 0;
                      return Math.max(0, total - produzido);
                    })()}
                  </span>
                </div>
                <div className="producao-card">
                  <span className="producao-label">Status</span>
                  <span className={`badge ${getBadgeStatus(pedidoHistorico.statusProducao || 'PENDENTE')}`}>
                    {pedidoHistorico.statusProducao || 'PENDENTE'}
                  </span>
                </div>
              </div>
              
              <div className="grade-pedido">
                <strong>Grade do Pedido:</strong>
                <div className="grade-display">
                  {formatarGrade(parseGrade(pedidoHistorico.gradeNumeracao))}
                </div>
              </div>
            </div>

            <div className="historico-lista">
              <h4>Ordens de Produção Criadas ({historico.length})</h4>
              
              {loadingHistorico ? (
                <div className="loading-historico">Carregando histórico...</div>
              ) : historico.length === 0 ? (
                <div className="sem-historico">
                  <p>📋 Nenhuma ordem de produção foi criada para este pedido ainda.</p>
                  <p style={{ fontSize: 13, color: '#666', marginTop: 8 }}>
                    As ordens de produção aparecerão aqui quando forem cadastradas.
                  </p>
                </div>
              ) : (
                <div className="historico-items">
                  {historico.map((item, index) => (
                    <div key={item.id} className="historico-item">
                      <div className="historico-header">
                        <div className="ordem-info">
                          <span className="ordem-numero">OP #{item.mapaOrdemProducao || item.ordemProducaoId}</span>
                          <span className="ordem-quantidade">{item.quantidadeProduzida} pares</span>
                          <span className={`badge-status ${getStatusBadgeOP(item)}`}>
                            {getStatusTextoOP(item)}
                          </span>
                        </div>
                        <div className="ordem-acoes">
                          <span className="ordem-data">
                            {new Date(item.dataProducao).toLocaleString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {!item.ordemConcluida && (
                            <button 
                              className="btn-concluir-op"
                              onClick={(e) => {
                                e.stopPropagation();
                                concluirOrdemProducao(item.ordemProducaoId);
                              }}
                              title="Marcar como concluída"
                            >
                              ✓ Concluir
                            </button>
                          )}
                          {item.ordemConcluida && (
                            <button 
                              className="btn-reabrir-op"
                              onClick={(e) => {
                                e.stopPropagation();
                                reabrirOrdemProducao(item.ordemProducaoId);
                              }}
                              title="Reabrir ordem"
                            >
                              ↺ Reabrir
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {item.ordemConcluida && item.dataConlusaoOrdem && (
                        <div className="info-conclusao">
                          ✓ Concluída em {new Date(item.dataConlusaoOrdem).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          {item.usuarioConclusao && ` por ${item.usuarioConclusao}`}
                        </div>
                      )}
                      
                      <div className="historico-detalhes">
                        {(item.dataInicio || item.previsaoTermino) && (
                          <div className="detalhe-item periodo-producao">
                            <strong>Período de Produção:</strong>
                            <span className="periodo-datas">
                              {item.dataInicio && (
                                <span className="data-inicio">
                                  📅 Início: {new Date(item.dataInicio).toLocaleDateString('pt-BR')}
                                </span>
                              )}
                              {item.previsaoTermino && (
                                <span className="data-final">
                                  🏁 Previsão: {new Date(item.previsaoTermino).toLocaleDateString('pt-BR')}
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                        
                        <div className="detalhe-item">
                          <strong>Grade Produzida:</strong>
                          <span className="grade-badge">{formatarGrade(item.gradeProduzida)}</span>
                        </div>
                        
                        {item.observacao && (
                          <div className="detalhe-item">
                            <strong>Observação:</strong>
                            <span>{item.observacao}</span>
                          </div>
                        )}
                        
                        {item.nomeUsuario && (
                          <div className="detalhe-item">
                            <strong>Usuário:</strong>
                            <span>{item.nomeUsuario}</span>
                          </div>
                        )}
                      </div>
                      
                      {index < historico.length - 1 && <div className="historico-divider"></div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-fechar" onClick={fecharHistorico}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      <ConfirmationModal
        open={modalConfirm.open}
        title={modalConfirm.title}
        message={modalConfirm.message}
        type={modalConfirm.type}
        confirmText={modalConfirm.confirmText}
        cancelText={modalConfirm.cancelText}
        onConfirm={() => {
          modalConfirm.onConfirm && modalConfirm.onConfirm();
        }}
        onCancel={() => setModalConfirm(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default ListagemPedidosVenda;

